/**
 * Les dépôts et les liens de dépôt du mode `?demo` (ARTO-129) : en mémoire,
 * sans réseau. Un dépôt de départ inventorié (un refus de chaque sorte), un
 * lien actif ; un envoi simule sa progression, puis l'inventaire accepte tout,
 * sauf un exécutable ou une page HTML (`type_interdit`) et un fichier dont le
 * nom contient « eicar » (`infecte`). Aucune galerie ni personne réelle.
 */
import type { TransportDepot } from '@artosera/shared';
import { compteDemoCourant } from './demoData.js';
import type { DepotVue, EntreeManifeste, LienVue, Manifeste, NouveauLien } from './depots.js';

const JOUR = 86_400_000;
const GIO = 1024 ** 3;

interface DepotDemo {
  vue: DepotVue;
  fichiers: EntreeManifeste[];
}

const parGalerie = new Map<string, { depots: DepotDemo[]; liens: LienVue[] }>();
let compteur = 0;

const identifiant = (prefixe: string): string => `${prefixe}-demo-${String((compteur += 1))}`;

function depotVide(idGalerie: string, libelle: string, plafondOctets: number): DepotDemo {
  return {
    vue: {
      id: identifiant('depot'),
      galerieId: idGalerie,
      dossierId: null,
      lienId: null,
      libelle,
      etat: 'ouvert',
      plafondOctets,
      octetsDeclares: 0,
      octetsRecus: 0,
      fichiers: 0,
      inventaire: { etat: 'vide', raison: null, faitLe: null, compteurs: null, manifeste: false },
      utilisable: false,
      envoi: { mode: 'api', tailleMorceau: 16 * 1024 * 1024, envoisParalleles: 4 },
      operateur: compteDemoCourant().compte.email,
      creeLe: new Date().toISOString(),
      purgeLe: null,
    },
    fichiers: [],
  };
}

const TYPES_PAR_EXTENSION: Record<string, string> = {
  csv: 'text/csv',
  json: 'application/json',
  xml: 'application/xml',
  vcf: 'text/vcard',
  txt: 'text/plain',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function entree(chemin: string, octets: number): EntreeManifeste {
  const nom = chemin.toLowerCase();
  const extension = nom.split('.').pop() ?? '';
  const mime = TYPES_PAR_EXTENSION[extension];
  const refus = nom.includes('eicar')
    ? 'infecte'
    : /\.(exe|html?|js)$/.test(nom)
      ? 'type_interdit'
      : null;
  return {
    id: identifiant('fichier'),
    chemin,
    octets,
    etat: refus === null ? 'accepte' : 'refuse',
    motif: refus,
    detail: null,
    sha256: null,
    type: mime === undefined || refus !== null ? null : { mime, ext: extension },
  };
}

/** Le dépôt d'images de la démo (ARTO-131) : ses noms disent des cas bien tranchés ou douteux. */
export const LIBELLE_PHOTOS = 'Photographies de l’atelier';
const FICHIERS_PHOTOS: readonly (readonly [string, number])[] = [
  ['Atelier/A-1.jpg', 4_812_800],
  ['Atelier/A-1_2.jpg', 3_901_440],
  ['Atelier/A-2.jpg', 5_120_000],
  ['Atelier/A-3.png', 7_340_032],
  ['Atelier/A-3 (2)-3.jpg', 2_990_080],
  ['Atelier/B-1.jpeg', 4_208_640],
  ['Atelier/B-1.jpg', 4_208_640],
  ['Atelier/B-2.webp', 1_310_720],
  ['Atelier/C-7.jpg', 6_291_456],
  ['Atelier/C-7_2.jpg', 5_767_168],
  ['Atelier/D-4.jpg', 3_145_728],
  ['Atelier/Z-9.jpg', 2_621_440],
  ['Atelier/Brouillons/IMG_0412.jpg', 1_835_008],
  ['Atelier/Scans/C-7_scan.tif', 48_234_496],
  ['Atelier/Scans/A-2_scan.tiff', 45_088_768],
];

function inventorier(depot: DepotDemo): void {
  const acceptes = depot.fichiers.filter((f) => f.etat === 'accepte');
  depot.vue.inventaire = {
    etat: 'fait',
    raison: null,
    faitLe: new Date().toISOString(),
    manifeste: true,
    compteurs: {
      acceptes: acceptes.length,
      refuses: depot.fichiers.length - acceptes.length,
      enAttente: 0,
      octetsAcceptes: acceptes.reduce((somme, f) => somme + f.octets, 0),
    },
  };
  depot.vue.utilisable = true;
}

function etat(idGalerie: string): { depots: DepotDemo[]; liens: LienVue[] } {
  const connu = parGalerie.get(idGalerie);
  if (connu) return connu;
  const depart = depotVide(idGalerie, 'Export de l’ancien logiciel', 50 * GIO);
  depart.vue.creeLe = new Date(Date.now() - 2 * JOUR).toISOString();
  depart.fichiers = [
    entree('Export/Contacts.csv', 184_320),
    entree('Export/Mailchimp-desabonnes.csv', 31_744),
    entree('Export/Oeuvres.xlsx', 912_384),
    entree('Export/Prix.csv', 142_848),
    entree('Export/Expositions.csv', 9_216),
    entree('Export/Anciens-stocks.xlsx', 61_440),
    entree('Export/Images/vue-atelier.jpg', 4_812_800),
    entree('Export/Outils/installe.exe', 102_400),
    entree('Export/notes/eicar.txt', 68),
  ];
  depart.vue.fichiers = depart.fichiers.length;
  depart.vue.octetsRecus = depart.fichiers.reduce((somme, f) => somme + f.octets, 0);
  depart.vue.octetsDeclares = depart.vue.octetsRecus;
  inventorier(depart);
  const photos = depotVide(idGalerie, LIBELLE_PHOTOS, 50 * GIO);
  photos.vue.creeLe = new Date(Date.now() - JOUR).toISOString();
  photos.fichiers = FICHIERS_PHOTOS.map(([chemin, octets]) => entree(chemin, octets));
  photos.fichiers.push(
    entree('Atelier/Contacts.csv', 6_144),
    entree('Atelier/Documents.xlsx', 18_432),
  );
  photos.vue.fichiers = photos.fichiers.length;
  photos.vue.octetsRecus = photos.fichiers.reduce((somme, f) => somme + f.octets, 0);
  photos.vue.octetsDeclares = photos.vue.octetsRecus;
  inventorier(photos);
  const nouveau = { depots: [depart, photos], liens: [] as LienVue[] };
  parGalerie.set(idGalerie, nouveau);
  return nouveau;
}

function trouver(idGalerie: string, depotId: string): DepotDemo {
  const depot = etat(idGalerie).depots.find((d) => d.vue.id === depotId);
  if (!depot) throw new Error('Dépôt introuvable.');
  return depot;
}

const attendre = (ms: number) =>
  new Promise<void>((resoudre) => {
    setTimeout(resoudre, ms);
  });

export const demoDepots = {
  depots(idGalerie: string): DepotVue[] {
    return etat(idGalerie).depots.map((depot) => ({ ...depot.vue }));
  },

  creer(idGalerie: string, corps: { libelle: string; plafondOctets: number }): DepotVue {
    const depot = depotVide(idGalerie, corps.libelle, corps.plafondOctets);
    etat(idGalerie).depots.unshift(depot);
    return { ...depot.vue };
  },

  inventorier(idGalerie: string, depotId: string): DepotVue {
    const depot = trouver(idGalerie, depotId);
    inventorier(depot);
    return { ...depot.vue };
  },

  /**
   * Ce qu'un import par connecteur verse dans son dépôt (ARTO-135) : les
   * fichiers arrivent, l'inventaire les passe en revue.
   */
  verser(
    idGalerie: string,
    depotId: string,
    fichiers: readonly (readonly [string, number])[],
  ): void {
    const depot = trouver(idGalerie, depotId);
    depot.fichiers = fichiers.map(([chemin, octets]) => entree(chemin, octets));
    depot.vue.fichiers = depot.fichiers.length;
    depot.vue.octetsRecus = depot.fichiers.reduce((somme, f) => somme + f.octets, 0);
    depot.vue.octetsDeclares = depot.vue.octetsRecus;
    inventorier(depot);
  },

  manifeste(idGalerie: string, depotId: string): Manifeste {
    const depot = trouver(idGalerie, depotId);
    return {
      etabliLe: depot.vue.inventaire.faitLe ?? new Date().toISOString(),
      utilisable: depot.vue.utilisable,
      compteurs: depot.vue.inventaire.compteurs ?? {
        acceptes: 0,
        refuses: 0,
        enAttente: 0,
        octetsAcceptes: 0,
      },
      pages: [{ fichiers: depot.fichiers.length }],
    };
  },

  pageManifeste(idGalerie: string, depotId: string): EntreeManifeste[] {
    return trouver(idGalerie, depotId).fichiers;
  },

  liens(idGalerie: string): LienVue[] {
    return etat(idGalerie).liens.map((lien) => ({ ...lien }));
  },

  creerLien(idGalerie: string, corps: NouveauLien): { lien: LienVue; url: string } {
    const depot = depotVide(idGalerie, corps.libelle, corps.octetsMax);
    const lien: LienVue = {
      id: identifiant('lien'),
      depotId: depot.vue.id,
      libelle: corps.libelle,
      etat: 'actif',
      expireLe: new Date(Date.now() + corps.dureeJours * JOUR).toISOString(),
      octetsMax: corps.octetsMax,
      octetsDeclares: 0,
      octetsRecus: 0,
      fichiers: 0,
      creePar: compteDemoCourant().compte.email,
      revoqueLe: null,
      creeLe: new Date().toISOString(),
    };
    depot.vue.lienId = lien.id;
    etat(idGalerie).depots.unshift(depot);
    etat(idGalerie).liens.unshift(lien);
    return { lien: { ...lien }, url: `${window.location.origin}/depot/demonstration-${lien.id}` };
  },

  revoquerLien(idGalerie: string, lienId: string): LienVue {
    const lien = etat(idGalerie).liens.find((l) => l.id === lienId);
    if (!lien) throw new Error('Lien introuvable.');
    lien.etat = 'revoque';
    lien.revoqueLe = new Date().toISOString();
    return { ...lien };
  },

  /** Un envoi qui avance par paliers, sans réseau. */
  transport(idGalerie: string, depotId: string): TransportDepot<Blob> {
    const depot = trouver(idGalerie, depotId);
    const enCours = new Map<string, { chemin: string; octets: number }>();
    return {
      async ouvrir(chemin, octets) {
        const id = identifiant('fichier');
        enCours.set(id, { chemin, octets });
        depot.vue.octetsDeclares += octets;
        depot.vue.fichiers += 1;
        return { fichierId: id, mode: 'api', dejaRecus: [] };
      },
      async signer() {
        return [];
      },
      async envoyer(_cible, corps, progres) {
        for (let pas = 1; pas <= 4; pas += 1) {
          await attendre(120);
          progres(Math.round((corps.size * pas) / 4));
        }
      },
      async terminer(fichierId) {
        const fichier = enCours.get(fichierId);
        if (!fichier) return;
        depot.fichiers.push(entree(fichier.chemin, fichier.octets));
        depot.vue.octetsRecus += fichier.octets;
        depot.vue.inventaire = { ...depot.vue.inventaire, etat: 'a_faire' };
      },
    };
  },
};
